import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { ArrowRight, LogIn, LogOut } from "lucide-react";
import { Button, buttonVariants } from "@repo/blacksmith-ui/button";
import { cn } from "@repo/blacksmith-ui/cn";
import type { FallbackOrg } from "@/hooks/useUrlOrgAccessGate";
import { githubReconnectLogin } from "@/lib/github-reauth";
import { instanceBasePath } from "@/lib/named-org";
export interface OrgAccessSplashProps {
  org: string;
  /** Org to offer instead; null when the user has no orgs. */
  fallbackOrg: FallbackOrg | null;
  username: string | null;
  onLogout: () => void;
  /** Defaults to the silent GitHub re-login; overridable for stories. */
  onReconnect?: () => void;
}

/**
 * Full-screen splash for an `/<org>/...` URL whose org is not in the user's
 * orgs, in the same pixel-terminal family as GlobalErrorSplash. The copy
 * deliberately doesn't say whether the org exists on Blacksmith.
 */
export function OrgAccessSplash(t0) {
  const $ = _c(27);
  const {
    org,
    fallbackOrg,
    username,
    onLogout,
    onReconnect: t1
  } = t0;
  const onReconnect = t1 === undefined ? githubReconnectLogin : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div aria-hidden={true} className="pointer-events-none absolute inset-0 bg-[image:radial-gradient(var(--pattern_color)_0.5px,_transparent_0)] bg-[size:16px_16px] opacity-60 [--pattern_color:var(--color-black)]/15 dark:[--pattern_color:var(--color-white)]/7" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="type-numerals-pixel-l text-neutral-900 dark:text-neutral-100">404</div>;
    t4 = <div className="mt-2 h-1 w-16 rounded-full bg-blacksmith-yellow" />;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  let t5;
  if ($[3] !== org) {
    t5 = <h1 className="mt-8 type-page-title-m text-neutral-900 dark:text-neutral-100">Can't open {org}</h1>;
    $[3] = org;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = username ? `@${username}` : "your account";
  let t7;
  if ($[5] !== t6) {
    t7 = <p className="mt-2 type-body-s-regular text-muted-foreground">This organization isn't on Blacksmith, or{" "}{t6} isn't a member.</p>;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="mt-2 type-body-s-regular text-muted-foreground">Just added, or the org uses SAML single sign-on? Reconnect GitHub to refresh your access.</p>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== fallbackOrg) {
    t9 = fallbackOrg && <Link href={`${instanceBasePath(fallbackOrg.instanceSlug)}/${encodeURIComponent(fallbackOrg.login)}/home`} className={cn(buttonVariants(), "h-8 gap-1 !pl-3 !pr-2.5")}>Go to {fallbackOrg.login}<ArrowRight className="size-4 shrink-0" /></Link>;
    $[8] = fallbackOrg;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== onReconnect) {
    t10 = <Button variant="secondary" icon={LogIn} onClick={onReconnect}>Reconnect GitHub</Button>;
    $[10] = onReconnect;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t9) {
    t11 = <div className="mt-6 flex items-center justify-center gap-2">{t9}{t10}</div>;
    $[12] = t10;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== username) {
    t12 = username && <span>signed in as @{username}</span>;
    $[15] = username;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== onLogout) {
    t13 = <Button variant="inline" icon={LogOut} onClick={onLogout} className="h-auto !p-0 type-code-block underline decoration-dotted underline-offset-4 [&_svg]:size-3.5">log out</Button>;
    $[17] = onLogout;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <div className="mt-8 flex w-full items-center justify-center gap-6 border-t border-[var(--color-border)] pt-4 type-code-block text-muted-foreground">{t12}{t13}</div>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t11 || $[23] !== t14 || $[24] !== t5 || $[25] !== t7) {
    t15 = <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-app-background p-6">{t2}<div className="relative flex max-w-md flex-col items-center text-center">{t3}{t4}{t5}{t7}{t8}{t11}{t14}</div></div>;
    $[22] = t11;
    $[23] = t14;
    $[24] = t5;
    $[25] = t7;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  return t15;
}